import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Box, Typography, Button, Chip, IconButton, Tooltip, ToggleButton, ToggleButtonGroup, Skeleton } from "@mui/material";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import LinkIcon from "@mui/icons-material/Link";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { normalizeCity, productTypeOf } from "../utils/productLabels";
import { logRegistrationEvent } from "../utils/funnelAnalytics";
import Markdown from "./Markdown";
import Carousel from "./register/Carousel";
import {
  FeaturedSlide,
  StatusBadge,
  openRegistration,
  programInfo,
  sortCityList,
  useRegistrationProducts,
} from "./register/programs";

function Meta({ icon, children }) {
  if (!children) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, color: "rgba(0,0,0,0.65)", fontSize: "0.875rem" }}>
      <Box component="span" sx={{ display: "flex", mt: "1px", "& svg": { fontSize: 18 } }}>
        {icon}
      </Box>
      <span>{children}</span>
    </Box>
  );
}

function CopyLinkButton({ onCopy }) {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? "Link copied" : "Copy registration link"}>
      <IconButton
        aria-label="Copy registration link"
        onClick={(e) => {
          onCopy(e).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
          });
        }}
        sx={{ border: "1px solid rgba(0,0,0,0.15)", borderRadius: 2 }}
      >
        {copied ? <CheckIcon fontSize="small" /> : <LinkIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
}

function ProgramCard({ info, onSelect, onCopy }) {
  const [open, setOpen] = useState(false);
  const long = info.markdown.split("\n").length > 4 || info.markdown.length > 220;
  return (
    <Box
      component="article"
      sx={{
        display: "flex",
        flexDirection: "column",
        bgcolor: "#fff",
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid rgba(0,0,0,0.08)",
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
        "&:hover": info.isSoldOut ? undefined : { boxShadow: "0 10px 30px rgba(0,0,0,0.08)", transform: "translateY(-2px)" },
      }}
    >
      <Box sx={{ position: "relative", aspectRatio: "16 / 10", bgcolor: "#eceef1", overflow: "hidden" }}>
        {info.product?.image && (
          <Box
            component="img"
            src={info.product.image}
            alt={info.title}
            loading="lazy"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center 30%",
              display: "block",
              filter: info.isSoldOut ? "grayscale(1)" : "none",
              opacity: info.isSoldOut ? 0.75 : 1,
            }}
          />
        )}
        <StatusBadge info={info} sx={{ position: "absolute", top: 12, left: 12 }} />
      </Box>

      <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.25, flex: 1 }}>
        <Box>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, letterSpacing: 1.2, textTransform: "uppercase", color: "rgba(0,0,0,0.45)" }}>
            {[info.city, info.subtitle].filter(Boolean).join(" · ")}
          </Typography>
          <Typography component="h3" sx={{ fontSize: "1.2rem", fontWeight: 800, lineHeight: 1.2, mt: 0.25, color: "#111" }}>
            {info.title}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          {info.prices.map((p) => (
            <Chip key={p} label={p} size="small" sx={{ fontWeight: 800, bgcolor: "#111", color: "#fff" }} />
          ))}
          {info.regLabel && <Chip label={info.regLabel} size="small" variant="outlined" sx={{ fontWeight: 600 }} />}
          {info.sport && <Chip label={info.sport} size="small" variant="outlined" sx={{ fontWeight: 600 }} />}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Meta icon={<PlaceOutlinedIcon />}>{info.location}</Meta>
          <Meta icon={<ScheduleIcon />}>{info.timing}</Meta>
        </Box>

        {info.markdown && (
          <Box sx={{ position: "relative" }}>
            <Box
              sx={{
                maxHeight: open || !long ? "none" : 104,
                overflow: "hidden",
                color: "rgba(0,0,0,0.75)",
                ...(open || !long ? null : { maskImage: "linear-gradient(180deg, #000 60%, transparent)" }),
              }}
            >
              <Markdown source={info.markdown} dense sx={{ color: "inherit", lineHeight: 1.5, "& > :first-of-type": { mt: 0 } }} />
            </Box>
            {long && (
              <Button
                size="small"
                onClick={() => setOpen((o) => !o)}
                endIcon={<ExpandMoreIcon sx={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />}
                sx={{ textTransform: "none", fontWeight: 700, color: "#111", px: 0, mt: 0.5 }}
              >
                {open ? "Show less" : "Show details"}
              </Button>
            )}
          </Box>
        )}

        <Box sx={{ display: "flex", gap: 1, mt: "auto", pt: 1 }}>
          <Button
            fullWidth
            variant="contained"
            disableElevation
            disabled={info.isSoldOut}
            onClick={onSelect}
            sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2, py: 1.1, bgcolor: "#111", "&:hover": { bgcolor: "#000" } }}
          >
            {info.isSoldOut ? "Sold out" : "Register"}
          </Button>
          <CopyLinkButton onCopy={onCopy} />
        </Box>
      </Box>
    </Box>
  );
}

const SelectDivision = () => {
  const { loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { products, paidCountsByKey } = useRegistrationProducts();
  const catalogMode = searchParams.get("type") === "tournament" ? "tournament" : "league";
  const cityParam = searchParams.get("city");

  useEffect(() => {
    logRegistrationEvent("reg_products_view", { page: "select_division", catalog: catalogMode });
  }, [catalogMode]);

  const programs = useMemo(
    () =>
      (products || [])
        .filter((p) => productTypeOf(p) === catalogMode)
        .map((p) => programInfo(p, paidCountsByKey))
        .sort(
          (a, b) =>
            Number(a.isSoldOut) - Number(b.isSoldOut) ||
            sortCityList([a.city, b.city])[0].localeCompare(a.city) ||
            (Number(a.product.division) || 0) - (Number(b.product.division) || 0) ||
            a.title.localeCompare(b.title)
        ),
    [products, catalogMode, paidCountsByKey]
  );

  const cities = useMemo(() => sortCityList([...new Set(programs.map((p) => p.city).filter(Boolean))]), [programs]);
  const city = cities.find((c) => normalizeCity(c) === normalizeCity(cityParam)) || null;
  const visible = city ? programs.filter((p) => normalizeCity(p.city) === normalizeCity(city)) : programs;
  const openNow = visible.filter((p) => !p.isSoldOut);

  const updateParams = (patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setSearchParams(next, { replace: true });
  };

  const setCatalogMode = (mode) => {
    if (!mode) return;
    updateParams({ type: mode === "tournament" ? "tournament" : null, city: null });
    logRegistrationEvent("reg_catalog_mode", { catalog: mode });
  };

  const setCity = (c) => {
    updateParams({ city: c });
    if (c) logRegistrationEvent("reg_city_expand", { city: c });
  };

  const handleSelect = (info) => {
    if (info.isSoldOut) return;
    logRegistrationEvent("reg_product_card_select", {
      city: info.city,
      division: info.divisionLabel,
      product_id: String(info.product?.id ?? ""),
      catalog: catalogMode,
    });
    openRegistration(navigate, info);
  };

  const copyRegistrationLink = (info, e) => {
    e.preventDefault();
    e.stopPropagation();
    const q = new URLSearchParams({ region: info.city, division: info.divisionLabel, productId: String(info.product?.id ?? "") });
    const url = `${window.location.origin}/register/apply?${q.toString()}`;
    return navigator.clipboard
      .writeText(url)
      .then(() => logRegistrationEvent("reg_product_copy_link", { product_id: String(info.product?.id ?? "") }));
  };

  if (loading) return null;

  const noun = catalogMode === "tournament" ? "tournament" : "league";

  return (
    <Box sx={{ px: { xs: 2, sm: 4, md: 6 }, pt: { xs: 3, md: 6 }, pb: 8, bgcolor: "#fafafa", minHeight: "80vh" }}>
      <Box sx={{ maxWidth: 1080, mx: "auto" }}>
        <Box component="header" sx={{ mb: { xs: 3, md: 4 } }}>
          <Typography sx={{ fontSize: "0.8rem", fontWeight: 800, letterSpacing: 2, textTransform: "uppercase", color: "rgba(0,0,0,0.45)" }}>
            Registration
          </Typography>
          <Typography component="h1" sx={{ fontSize: { xs: "2.1rem", sm: "2.8rem", md: "3.3rem" }, fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1.05, color: "#111", mt: 0.5 }}>
            {catalogMode === "tournament" ? "Find your tournament" : "Find your league"}
          </Typography>
          <Typography sx={{ color: "rgba(0,0,0,0.6)", mt: 1.25, maxWidth: 560, fontSize: { xs: "0.95rem", md: "1.05rem" } }}>
            Pick a program, register your team or yourself, and you're in. Spots are limited and go first come, first served.
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5, mt: 3 }}>
            <ToggleButtonGroup
              exclusive
              value={catalogMode}
              onChange={(_, v) => setCatalogMode(v)}
              size="small"
              sx={{
                bgcolor: "#fff",
                borderRadius: 999,
                p: 0.5,
                border: "1px solid rgba(0,0,0,0.1)",
                "& .MuiToggleButton-root": {
                  border: 0,
                  borderRadius: "999px !important",
                  px: 2.25,
                  textTransform: "none",
                  fontWeight: 700,
                  color: "rgba(0,0,0,0.6)",
                },
                "& .Mui-selected": { bgcolor: "#111 !important", color: "#fff !important" },
              }}
            >
              <ToggleButton value="league">Leagues</ToggleButton>
              <ToggleButton value="tournament">Tournaments</ToggleButton>
            </ToggleButtonGroup>
            {cities.length > 1 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {[null, ...cities].map((c) => {
                  const selected = (c || null) === city;
                  return (
                    <Chip
                      key={c || "all"}
                      label={c || "All cities"}
                      onClick={() => setCity(c)}
                      variant={selected ? "filled" : "outlined"}
                      sx={{
                        fontWeight: 700,
                        bgcolor: selected ? "#111" : "#fff",
                        color: selected ? "#fff" : "rgba(0,0,0,0.7)",
                        borderColor: "rgba(0,0,0,0.12)",
                        "&:hover": { bgcolor: selected ? "#000" : "rgba(0,0,0,0.04)" },
                      }}
                    />
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>

        {products === null ? (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }, gap: 2.5 }}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} variant="rounded" height={380} sx={{ borderRadius: 3 }} />
            ))}
          </Box>
        ) : (
          <>
            {openNow.length > 0 && (
              <Box component="section" sx={{ mb: { xs: 4, md: 6 } }}>
                <Typography component="h2" sx={{ fontWeight: 900, fontSize: "1.25rem", mb: 1.5, color: "#111" }}>
                  Open now
                </Typography>
                <Carousel label={`Open ${noun}s`}>
                  {openNow.map((info) => (
                    <FeaturedSlide key={info.product.id} info={info} onSelect={() => handleSelect(info)} />
                  ))}
                </Carousel>
              </Box>
            )}

            <Box component="section">
              <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 1.5 }}>
                <Typography component="h2" sx={{ fontWeight: 900, fontSize: "1.25rem", color: "#111" }}>
                  All {noun}s
                </Typography>
                <Typography sx={{ color: "rgba(0,0,0,0.5)", fontSize: "0.85rem" }}>
                  {openNow.length} open · {visible.length - openNow.length} sold out
                </Typography>
              </Box>
              {visible.length === 0 ? (
                <Box sx={{ p: 4, textAlign: "center", bgcolor: "#fff", borderRadius: 3, border: "1px dashed rgba(0,0,0,0.15)", color: "rgba(0,0,0,0.55)" }}>
                  No {noun}s open{city ? ` in ${city}` : ""} right now. Follow @hadafsoccerleague for the next registration window.
                </Box>
              ) : (
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }, gap: 2.5 }}>
                  {visible.map((info) => (
                    <ProgramCard
                      key={info.product.id}
                      info={info}
                      onSelect={() => handleSelect(info)}
                      onCopy={(e) => copyRegistrationLink(info, e)}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default SelectDivision;
